import type { Issue, PanelPart, Vec3 } from './model.js';
import type { AssemblyInstance, AssemblyPanel, FrontSpaceRef, ShelfSet } from './document.js';
import { makePanel } from './geometry.js';
import { panelSize } from './assembly.js';

/**
 * Shelf sets ("Police"): a parametric group of horizontal shelves between two boundary panels of an
 * assembly (bottom and top), inside one compartment (between two vertical panels, or the full width of
 * the boundary when there are none). Shelves are generated on every evaluation, so they follow the
 * boundaries when the carcass changes.
 */

const TOL = 500; // 0.5 mm
export const MAX_SHELVES = 50;

interface Box {
  min: Vec3;
  max: Vec3;
}

/** Boxes of a panel (one per repeated copy). */
function boxes(p: AssemblyPanel): Box[] {
  const size = panelSize(p);
  const n = p.repeat?.count ?? 1;
  const out: Box[] = [];
  for (let i = 0; i < n; i++) {
    const min: Vec3 = [...p.position];
    if (p.repeat) {
      const k = ({ x: 0, y: 1, z: 2 } as const)[p.repeat.axis];
      min[k] += i * p.repeat.spacing;
    }
    out.push({ min, max: [min[0] + size[0], min[1] + size[1], min[2] + size[2]] });
  }
  return out;
}
const box = (p: AssemblyPanel): Box => boxes(p)[0]!;

export interface Compartment {
  leftKey: string | null;
  rightKey: string | null;
  /** Free space between the boundaries (local µm), before the side clearance. */
  min: Vec3;
  max: Vec3;
}

export interface ShelfBoundaries {
  bottomKey: string;
  topKey: string;
  /** Boundary whose length/depth the shelves take when there are no side panels (the second one picked). */
  refKey: string;
  compartments: Compartment[];
  /** Bottom and top boundaries differ in width or depth. */
  mismatch: boolean;
}

/**
 * Checks two picked panels as shelf boundaries and finds the compartments between them (split by
 * vertical side panels that stand in the gap). `secondKey` is the panel picked second.
 */
export function shelfBoundaries(inst: AssemblyInstance, firstKey: string, secondKey: string): ShelfBoundaries | { error: string } {
  const a = inst.panels.find((p) => p.key === firstKey);
  const b = inst.panels.find((p) => p.key === secondKey);
  if (!a || !b || a === b) return { error: 'Izaberite dvije ploče istog sklopa.' };
  if (a.orientation !== 'horizontal' || b.orientation !== 'horizontal') return { error: 'Granične ploče za police moraju biti horizontalne (gornja i donja).' };
  if ((a.repeat && a.repeat.count > 1) || (b.repeat && b.repeat.count > 1)) return { error: 'Granična ploča ne smije imati ponavljanje.' };
  const [lo, hi] = box(a).min[1] <= box(b).min[1] ? [a, b] : [b, a];
  const L = box(lo);
  const H = box(hi);
  if (H.min[1] - L.max[1] <= TOL) return { error: 'Između izabranih ploča nema prostora.' };
  const R = box(b);
  const mismatch = Math.abs(L.min[0] - H.min[0]) > TOL || Math.abs(L.max[0] - H.max[0]) > TOL || Math.abs(L.min[2] - H.min[2]) > TOL || Math.abs(L.max[2] - H.max[2]) > TOL;

  const y0 = L.max[1];
  const y1 = H.min[1];
  const walls: { key: string; b: Box }[] = [];
  for (const p of inst.panels) {
    if (p.orientation !== 'vertical-side' || p === a || p === b) continue;
    for (const q of boxes(p)) {
      const inGap = q.min[1] < y1 - TOL && q.max[1] > y0 + TOL;
      const inDepth = Math.min(q.max[2], R.max[2]) - Math.max(q.min[2], R.min[2]) > TOL;
      const near = q.max[0] >= R.min[0] - TOL && q.min[0] <= R.max[0] + TOL;
      if (inGap && inDepth && near) walls.push({ key: p.key, b: q });
    }
  }
  walls.sort((x, y) => x.b.min[0] - y.b.min[0]);
  const compartments: Compartment[] = [];
  let cur: { x: number; key: string | null } = { x: R.min[0], key: null };
  let closed = false;
  const close = (x: number, key: string | null) => {
    if (x - cur.x > TOL) compartments.push({ leftKey: cur.key, rightKey: key, min: [cur.x, y0, R.min[2]], max: [x, y1, R.max[2]] });
  };
  for (const w of walls) {
    if (closed) break;
    if (w.b.max[0] <= R.min[0] + TOL) cur = { x: w.b.max[0], key: w.key };
    else if (w.b.min[0] >= R.max[0] - TOL) {
      close(w.b.min[0], w.key);
      closed = true;
    } else {
      close(w.b.min[0], w.key);
      cur = { x: w.b.max[0], key: w.key };
    }
  }
  if (!closed) close(R.max[0], null);
  return { bottomKey: lo.key, topKey: hi.key, refKey: b.key, compartments, mismatch };
}

/**
 * Depth (z range, local µm) of an opening: the overlap of the panels around it — as deep as the
 * shallowest one — cut by panels across the opening (a back panel behind, a panel in front).
 */
export function openingDepth(inst: AssemblyInstance, keys: (string | null | undefined)[], rect: { x0: number; x1: number; y0: number; y1: number }): { z0: number; z1: number } | null {
  const around = keys.map((k) => (k ? inst.panels.find((p) => p.key === k) : undefined)).filter((p): p is AssemblyPanel => !!p);
  if (!around.length) return null;
  let z0 = Math.max(...around.map((p) => box(p).min[2]));
  let z1 = Math.min(...around.map((p) => box(p).max[2]));
  const mid = (z0 + z1) / 2;
  for (const p of inst.panels) {
    if (p.orientation !== 'vertical-front' || around.includes(p)) continue;
    for (const q of boxes(p)) {
      const across = Math.min(q.max[0], rect.x1) - Math.max(q.min[0], rect.x0) > TOL && Math.min(q.max[1], rect.y1) - Math.max(q.min[1], rect.y0) > TOL;
      if (!across) continue;
      if ((q.min[2] + q.max[2]) / 2 < mid) z0 = Math.max(z0, q.max[2]);
      else z1 = Math.min(z1, q.min[2]);
    }
  }
  return { z0, z1 };
}

/** Why shelves cannot go into an opening (null = they can). */
export function shelfOpeningError(inst: AssemblyInstance, ref: Pick<FrontSpaceRef, 'left' | 'right' | 'bottom' | 'top' | 'drawer'>): string | null {
  const horiz = (k: string | null) => !!k && inst.panels.some((p) => p.key === k && p.orientation === 'horizontal' && !(p.repeat && p.repeat.count > 1));
  if (ref.drawer) return 'U prostoru su ladice.';
  if (!horiz(ref.bottom) || !horiz(ref.top)) return 'Prostor za police mora imati ploču ispod i iznad.';
  if ((inst.drawerSets ?? []).some((d) => d.bottomKey === ref.bottom && d.topKey === ref.top && d.leftKey === ref.left && d.rightKey === ref.right)) return 'U prostoru su ladice.';
  if ((inst.shelfSets ?? []).some((s) => s.leftKey === ref.left && s.rightKey === ref.right && s.bottomKey === ref.bottom && s.topKey === ref.top)) return 'U ovom prostoru već su police.';
  return null;
}

/** A new shelf set in a picked opening (the panels around it bound the shelves). */
export function makeShelfSetInSpace(inst: AssemblyInstance, ref: Pick<FrontSpaceRef, 'left' | 'right' | 'bottom' | 'top'>, count = 2): ShelfSet {
  const bottom = inst.panels.find((p) => p.key === ref.bottom)!;
  const n = (inst.shelfSets ?? []).length + 1;
  return {
    id: nextShelfSetId(inst),
    label: n === 1 ? 'Police' : `Police ${n}`,
    bottomKey: ref.bottom!,
    topKey: ref.top!,
    refKey: ref.bottom!,
    leftKey: ref.left,
    rightKey: ref.right,
    count,
    gap: ref.left || ref.right ? 1000 : 0,
    thickness: bottom.thickness,
    deleted: [],
    materialSlot: bottom.materialSlot,
    grain: bottom.grain,
  };
}

export function nextShelfSetId(inst: AssemblyInstance): string {
  const used = new Set([...inst.panels.map((p) => p.key), ...(inst.shelfSets ?? []).map((s) => s.id)]);
  let n = (inst.shelfSets ?? []).length + 1;
  while (used.has(`police-${n}`)) n++;
  return `police-${n}`;
}

/** A new shelf set in a compartment; thickness defaults to the reference boundary. */
export function makeShelfSet(inst: AssemblyInstance, b: ShelfBoundaries, c: Compartment, count = 2): ShelfSet {
  const ref = inst.panels.find((p) => p.key === b.refKey)!;
  const n = (inst.shelfSets ?? []).length + 1;
  return {
    id: nextShelfSetId(inst),
    label: n === 1 ? 'Police' : `Police ${n}`,
    bottomKey: b.bottomKey,
    topKey: b.topKey,
    refKey: b.refKey,
    leftKey: c.leftKey,
    rightKey: c.rightKey,
    count,
    gap: c.leftKey || c.rightKey ? 1000 : 0,
    thickness: ref.thickness,
    deleted: [],
    materialSlot: ref.materialSlot,
    grain: ref.grain,
  };
}

/** Shelf parts of a set (deleted ones left out) plus issues; evenly spaced in the free height. */
export function buildShelfSet(inst: AssemblyInstance, set: ShelfSet): { parts: PanelPart[]; issues: Issue[] } {
  const issues: Issue[] = [];
  const warn = (message: string, severity: 'warning' | 'error' = 'warning') => issues.push({ severity, code: 'shelves', message: `${set.label}: ${message}`, cabinetId: inst.id });
  const get = (k: string | null) => (k ? inst.panels.find((p) => p.key === k) : undefined);
  const bottom = get(set.bottomKey);
  const top = get(set.topKey);
  const ref = get(set.refKey) ?? bottom;
  if (!bottom || !top || !ref) {
    warn('granična ploča više ne postoji — police se ne prave.');
    return { parts: [], issues };
  }
  const L = box(bottom);
  const H = box(top);
  const left = get(set.leftKey);
  const right = get(set.rightKey);
  if ((set.leftKey && !left) || (set.rightKey && !right)) warn('bočna ploča prostora više ne postoji — uzeta je širina granične ploče.');
  // Without a side panel: as wide as both boundaries allow.
  const x0 = left ? box(left).max[0] + set.gap : Math.max(L.min[0], H.min[0]);
  const x1 = right ? box(right).min[0] - set.gap : Math.min(L.max[0], H.max[0]);
  const y0 = L.max[1];
  const free = H.min[1] - y0;
  // Depth: the shallowest panel around the space (stopping at a back panel), plus the offsets.
  const depth = openingDepth(inst, [set.bottomKey, set.topKey, left?.key, right?.key], { x0, x1, y0, y1: H.min[1] })!;
  const z0 = depth.z0 - (set.depthBack ?? 0);
  const z1 = depth.z1 + (set.depthFront ?? 0);
  const n = set.count;
  const t = set.thickness;
  const spacing = (free - n * t) / (n + 1);
  if (x1 - x0 <= 0 || spacing <= 0 || z1 - z0 <= 0) {
    warn('police ne staju u izabrani prostor.', 'error');
    return { parts: [], issues };
  }
  const parts: PanelPart[] = [];
  for (let i = 1; i <= n; i++) {
    if (set.deleted.includes(i)) continue;
    const y = Math.round(y0 + i * spacing + (i - 1) * t);
    parts.push(
      makePanel({
        cabinetId: inst.id,
        localKey: `${set.id}-${i}`,
        role: 'custom',
        label: `${set.label} ${i}`,
        box: { min: [x0, y, z0], size: [x1 - x0, t, z1 - z0 - (set.setbacks?.[String(i)] ?? 0)] as Vec3 },
        lengthAxis: 'x',
        widthAxis: 'z',
        materialSlot: set.materialSlot,
        grain: set.grain,
      }),
    );
  }
  return { parts, issues };
}

/** Which shelf set (and shelf number) a part local key belongs to. */
export function shelfOfPart(inst: AssemblyInstance, localKey: string): { set: ShelfSet; index: number } | null {
  for (const set of inst.shelfSets ?? []) {
    const m = localKey.startsWith(`${set.id}-`) ? Number(localKey.slice(set.id.length + 1)) : NaN;
    if (Number.isInteger(m) && m >= 1) return { set, index: m };
  }
  return null;
}

export type ShelfLinkProperty = 'count' | 'gap' | 'thickness';
export const SHELF_PROPERTY_LABEL: Record<ShelfLinkProperty, string> = { count: 'Broj polica', gap: 'Zazor', thickness: 'Debljina' };

// ---------- shelves when panels are added or removed ----------

const sameStyle = (a: ShelfSet, b: ShelfSet) => a.count === b.count && a.gap === b.gap && a.thickness === b.thickness;

/** Free space of a shelf set: between its side panels (or the reference width) and bottom/top. */
function setRegion(inst: AssemblyInstance, set: ShelfSet): { x0: number; x1: number; y0: number; y1: number } | null {
  const get = (k: string | null) => (k ? inst.panels.find((p) => p.key === k) : undefined);
  const bottom = get(set.bottomKey);
  const top = get(set.topKey);
  const ref = get(set.refKey) ?? bottom;
  if (!bottom || !top || !ref) return null;
  const l = get(set.leftKey);
  const r = get(set.rightKey);
  return { x0: l ? box(l).max[0] : box(ref).min[0], x1: r ? box(r).min[0] : box(ref).max[0], y0: box(bottom).max[1], y1: box(top).min[1] };
}

function nextSetIdAfter(inst: AssemblyInstance, extra: ShelfSet[]): string {
  return nextShelfSetId({ ...inst, shelfSets: [...(inst.shelfSets ?? []), ...extra] });
}

/**
 * A new panel cuts the space of shelf sets: a vertical panel through horizontal shelves splits each
 * set into a left and a right set (same settings, independent afterwards); a horizontal panel keeps
 * the set below it and gives the space above a new set with the same settings. Mutates `inst`;
 * returns the sets that were added.
 */
export function splitShelvesByPanel(inst: AssemblyInstance, panel: AssemblyPanel): ShelfSet[] {
  const p = box(panel);
  const added: ShelfSet[] = [];
  for (const set of [...(inst.shelfSets ?? [])]) {
    const r = setRegion(inst, set);
    if (!r) continue;
    if (panel.orientation === 'vertical-side') {
      const inside = p.min[0] > r.x0 + TOL && p.max[0] < r.x1 - TOL;
      const fullHeight = p.min[1] <= r.y0 + TOL && p.max[1] >= r.y1 - TOL;
      if (!inside || !fullHeight) continue;
      const right: ShelfSet = { ...structuredClone(set), id: nextSetIdAfter(inst, added), label: `${set.label} D`, leftKey: panel.key };
      set.label = `${set.label} L`;
      set.rightKey = panel.key;
      added.push(right);
    } else if (panel.orientation === 'horizontal') {
      const inside = p.min[1] > r.y0 + TOL && p.max[1] < r.y1 - TOL;
      const fullWidth = p.min[0] <= r.x0 + TOL && p.max[0] >= r.x1 - TOL;
      if (!inside || !fullWidth) continue;
      // The reference board of each part must be one of its own boundaries.
      const oldBottom = set.bottomKey;
      const oldTop = set.topKey;
      const upper: ShelfSet = { ...structuredClone(set), id: nextSetIdAfter(inst, added), label: `${set.label} gore`, bottomKey: panel.key, refKey: set.refKey === oldBottom ? panel.key : set.refKey };
      set.topKey = panel.key;
      if (set.refKey === oldTop) set.refKey = panel.key;
      added.push(upper);
    }
  }
  inst.shelfSets = [...(inst.shelfSets ?? []), ...added];
  return added;
}

export interface ShelfConflict {
  /** Left/bottom set and right/top set that would merge but have different settings. */
  first: string;
  second: string;
  axis: 'x' | 'y';
}

export interface PanelRemovalPlan {
  /** Pairs with the same settings that just merge. */
  merges: { first: string; second: string; axis: 'x' | 'y' }[];
  /** Pairs with different settings: the user picks whose settings stay. */
  conflicts: ShelfConflict[];
  /** Sets that get a new boundary further away. */
  extended: string[];
  /** Sets left without anything to hang on: deleted with the panel. */
  orphans: string[];
}

/** Nearest panel beyond the removed one on a side of a shelf region (for sets that lose a boundary). */
function wallBeyond(inst: AssemblyInstance, removed: string, r: { x0: number; x1: number; y0: number; y1: number }, side: 'left' | 'right' | 'bottom' | 'top'): string | null {
  let best: { key: string; d: number } | null = null;
  for (const p of inst.panels) {
    if (p.key === removed || (p.repeat && p.repeat.count > 1)) continue;
    const b = box(p);
    if (side === 'left' || side === 'right') {
      if (p.orientation !== 'vertical-side' || Math.min(b.max[1], r.y1) - Math.max(b.min[1], r.y0) < (r.y1 - r.y0) / 2) continue;
      const d = side === 'right' ? b.min[0] - r.x0 : r.x1 - b.max[0];
      if (d > TOL && (!best || d < best.d)) best = { key: p.key, d };
    } else {
      if (p.orientation !== 'horizontal' || Math.min(b.max[0], r.x1) - Math.max(b.min[0], r.x0) < (r.x1 - r.x0) / 2) continue;
      const d = side === 'top' ? b.min[1] - r.y0 : r.y1 - b.max[1];
      if (d > TOL && (!best || d < best.d)) best = { key: p.key, d };
    }
  }
  return best?.key ?? null;
}

/** What happens to the shelf sets if the panel `key` is deleted. */
export function planPanelRemoval(inst: AssemblyInstance, key: string): PanelRemovalPlan {
  const plan: PanelRemovalPlan = { merges: [], conflicts: [], extended: [], orphans: [] };
  const sets = inst.shelfSets ?? [];
  const paired = new Set<string>();
  for (const a of sets) {
    for (const b of sets) {
      if (a === b || paired.has(a.id) || paired.has(b.id)) continue;
      const x = a.rightKey === key && b.leftKey === key && a.bottomKey === b.bottomKey && a.topKey === b.topKey;
      const y = a.topKey === key && b.bottomKey === key && a.leftKey === b.leftKey && a.rightKey === b.rightKey;
      if (!x && !y) continue;
      paired.add(a.id).add(b.id);
      (sameStyle(a, b) ? plan.merges : plan.conflicts).push({ first: a.id, second: b.id, axis: x ? 'x' : 'y' });
    }
  }
  for (const s of sets) {
    if (paired.has(s.id)) continue;
    const sides = (['left', 'right', 'bottom', 'top'] as const).filter((side) => s[`${side}Key`] === key);
    if (!sides.length) continue;
    const r = setRegion(inst, s);
    const ok = r && sides.every((side) => wallBeyond(inst, key, r, side));
    (ok ? plan.extended : plan.orphans).push(s.id);
  }
  return plan;
}

/**
 * Deletes the panel and fixes the shelf sets that used it (see `planPanelRemoval`). `keep` picks, per
 * conflict (`first:second`), whose settings stay; default: the first (left / bottom) one.
 */
export function removePanelWithShelves(inst: AssemblyInstance, key: string, keep: Record<string, 'first' | 'second'> = {}): void {
  const plan = planPanelRemoval(inst, key);
  const sets = inst.shelfSets ?? [];
  const byId = (id: string) => sets.find((s) => s.id === id)!;
  const drop = new Set<string>(plan.orphans);
  for (const m of [...plan.merges, ...plan.conflicts]) {
    const a = byId(m.first);
    const b = byId(m.second);
    const style = keep[`${m.first}:${m.second}`] === 'second' ? b : a;
    const merged: ShelfSet = {
      ...structuredClone(style),
      id: a.id,
      label: a.label.replace(/ (L|D|gore)$/, ''),
      leftKey: a.leftKey,
      rightKey: m.axis === 'x' ? b.rightKey : a.rightKey,
      bottomKey: a.bottomKey,
      topKey: m.axis === 'y' ? b.topKey : a.topKey,
      refKey: a.refKey === key ? b.refKey : a.refKey,
    };
    sets[sets.indexOf(a)] = merged;
    drop.add(b.id);
  }
  for (const id of plan.extended) {
    const s = byId(id);
    const r = setRegion(inst, s)!;
    for (const side of ['left', 'right', 'bottom', 'top'] as const) if (s[`${side}Key`] === key) s[`${side}Key`] = wallBeyond(inst, key, r, side)!;
    if (s.refKey === key) s.refKey = s.bottomKey;
  }
  inst.shelfSets = sets.filter((s) => !drop.has(s.id));
  inst.panels = inst.panels.filter((p) => p.key !== key);
  for (const p of inst.panels) p.joints = p.joints.filter((j) => j.key !== key);
}
